/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Building, FileText, Plus, Users } from 'lucide-react';
import { DashboardSummaryCards } from '../../components/dashboard/DashboardSummaryCards';
import { CompanyService } from '../../services/company.service';
import { CompanyType } from '../../types/CompanyType';
import { useNavigate } from 'react-router-dom';
import { CompanyModal } from '../../components/company/CompanyModal';
import { UserModal } from '../../components/user/UserModal';
import { DashboardService } from '../../services/dashboard.service';
import { UserService } from '../../services/user.service';

export const SuperAdminDashboard = () => {
  const [stats, setStats] = useState({ companies: 0, users: 0, invites: 0, files: 0 });
  const [recentCompanies, setRecentCompanies] = useState<any[]>([]);
  const [recentInvitations, setRecentInvitations] = useState<any[]>([]);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [isCompaniesLoading, setIsCompaniesLoading] = useState(true);
  const [isInvitesLoading, setIsInvitesLoading] = useState(true);

  const navigate = useNavigate();

  const loadStats = async () => {
    setIsStatsLoading(true);
    try {
      const res = await DashboardService.getStats();
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
    } finally {
      setIsStatsLoading(false);
    }
  };

  const loadRecentCompanies = async () => {
    setIsCompaniesLoading(true);
    try {
      const res = await CompanyService.getCompanies();
      setRecentCompanies(res.data.slice(0, 5));
    } catch (err) {
      console.error('Failed to fetch companies', err);
    } finally {
      setIsCompaniesLoading(false);
    }
  };

  const loadRecentInvitations = async () => {
    setIsInvitesLoading(true);
    try {
      const res = await UserService.getAllInvitations();
      setRecentInvitations(res.data.slice(0, 5));
    } catch (err) {
      console.error('Failed to fetch invitations', err);
    } finally {
      setIsInvitesLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    loadRecentCompanies();
    loadRecentInvitations();
  }, []);

  return (
    <div>
      <DashboardSummaryCards
        cards={[
          { title: 'Companies', value: stats.companies, icon: <Building /> },
          { title: 'Users', value: stats.users, icon: <Users /> },
          { title: 'Invitations', value: stats.invites, icon: <Plus /> },
          { title: 'Files', value: stats.files, icon: <FileText /> },
        ]}
      />

      <div className="flex flex-col mt-8 lg:flex-row">
        <div className="w-full space-y-10 lg:w-[77%]">
          <div className="sm:w-[85%]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-primary">Recent Companies</h2>
              <a href="/companies" className="text-sm font-medium text-primary hover:underline">
                View All
              </a>
            </div>
            {isCompaniesLoading ? (
              <p className="text-sm text-muted-foreground text-primary">Loading...</p>
            ) : recentCompanies.length > 0 ? (
              <ul className="space-y-3">
                {recentCompanies.map((company) => (
                  <li
                    key={company.id}
                    className="flex items-center justify-between p-4 bg-white border rounded-md shadow-sm "
                  >
                    <div className="flex items-center gap-4">
                      {company.logo ? (
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL}${company.logo}`}
                          alt={`${company.name} logo`}
                          className="object-cover w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-muted text-muted-foreground">
                          <Building className="w-5 h-5 text-primary" />
                        </div>
                      )}

                      <div>
                        <p className="text-sm font-medium text-primary">{company.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Country: {company.country ? company.country.name : 'N/A'} | Users:{' '}
                          {company.usersCount ?? 'N/A'}
                        </p>
                      </div>
                    </div>

                    <span
                      className={`px-2 py-1 text-sm rounded-full font-medium ${
                        company.isActive
                          ? 'bg-green-100 text-green-700 border'
                          : 'bg-red-100 text-red-700 border'
                      }`}
                    >
                      {company.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground text-primary">
                No companies available yet.
              </p>
            )}
          </div>

          <div className=" sm:w-[85%]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-primary">Recent Invitations</h2>
              <a href="/invitations" className="text-sm font-medium text-primary hover:underline">
                View All
              </a>
            </div>
            {isInvitesLoading ? (
              <p className="text-sm text-muted-foreground text-primary">Loading...</p>
            ) : recentInvitations.length > 0 ? (
              <ul className="space-y-3">
                {recentInvitations.map((invite) => (
                  <li key={invite.id} className="p-4 bg-white border rounded-md shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-primary">{invite.email}</p>
                        <p className="text-xs text-muted-foreground">
                          Role: {invite.role?.name || invite.role}
                          {invite.company?.name ? ` | Company: ${invite.company.name}` : ''}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 text-sm rounded-full font-medium ${
                          invite.status === 'accepted'
                            ? 'bg-green-100 text-green-700 border'
                            : invite.status === 'revoked'
                              ? 'bg-red-100 text-red-700 border'
                              : invite.status === 'expired'
                                ? 'bg-yellow-100 text-yellow-800 border'
                                : 'bg-blue-100 text-blue-700 border'
                        }`}
                      >
                        {invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground text-primary">No invitations found.</p>
            )}
          </div>
        </div>

        <div className="w-full lg:w-[23%]">
          <div>
            <h2 className="mt-6 mb-4 text-lg font-semibold lg:mt-0 text-primary">Quick Actions</h2>
            <div className="space-y-4">
              <button
                className="flex items-center w-full gap-3 px-4 py-3 border rounded-lg text-primary hover:bg-muted "
                onClick={() => setIsCompanyModalOpen(true)}
              >
                <Building className="w-5 h-5" />
                Create Company
              </button>
              <button
                className="flex items-center w-full gap-3 px-4 py-3 border rounded-lg text-primary hover:bg-muted"
                onClick={() => setIsUserModalOpen(true)}
              >
                <Users className="w-5 h-5" />
                Add User
              </button>
              <button
                className="flex items-center w-full gap-3 px-4 py-3 border rounded-lg text-primary hover:bg-muted"
                onClick={() => navigate('/invitations')}
              >
                <Plus className="w-5 h-5" />
                View Invitations
              </button>
            </div>
          </div>
        </div>
      </div>

      <CompanyModal
        isOpen={isCompanyModalOpen}
        onClose={() => setIsCompanyModalOpen(false)}
        onSuccess={() => {
          loadRecentCompanies();
          setIsCompanyModalOpen(false);
        }}
      />

      <UserModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        onSuccess={() => {
          loadRecentCompanies();
          setIsUserModalOpen(false);
        }}
      />
    </div>
  );
};
